import React, { useState } from 'react';
import { View, TouchableOpacity, Image } from 'react-native';
import Map, { MapMouseEvent } from '../../../components/Map';
import { translate } from '../../../services/translate';
import { cameraService, GetCamerasMapParameters } from '../../../services/central-api/cameras';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useAuth } from '../../../context/Auth';
import PointOfInterestModal from './PointOfInterestModal';
import { useIsFocused } from '@react-navigation/native';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import MapFilter from './MapFilter';
import MapSearchDropdown from './MapSearchDropdown';
import { WindowInformation } from '../../../services/window-information';
import MapPolygonDropdown from '../../../components/MapPolygonDropdown';
import Container from '../../../components/Container';
import { useQuery } from '@tanstack/react-query';
import { openInNewTab } from '../../../services/navigation-assistant';

export default function Cameras() {
    const navigation = useNavigation<NavigationProp<CamerasParamList>>();
    const windowInfo = WindowInformation();

    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const [filters, setFilters] = useState<GetCamerasMapParameters>({
        online: true,
        offline: false,
        types: [],
        tags: [],
        integrationType: [],
        polygonType: undefined,
        polygon: undefined
    });

    const [polygons, setPolygons] = useState<MapPolygon[]>([]);
    const [location, setLocation] = useState<{ lat: number; lng: number; } | LatLng | undefined>();
    const [createPointOfInterest, setCreatePointOfInterest] = useState(false);
    const [latitude, setLatitude] = useState<string>('');
    const [longitude, setLongitude] = useState<string>('');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [showMapPoi, setShowMapPoi] = useState<boolean>(false);

    function pinClick(pin: Marker, event: MapMouseEvent) {
        if (pin?.metadata?.camera?.id) {
            const isModifierClick = event.domEvent.button === 1 || event.domEvent.ctrlKey || event.domEvent.metaKey;

            goToCamera(pin.metadata.camera.id, isModifierClick);
        }
    }

    function goToCamera(id: string, newTab: boolean) {
        if (newTab) {
            openInNewTab('Cameras', { screen: 'CameraMapPlayer', params: { id } });
            return;
        }
        navigation.navigate('CameraMapPlayer', { id });
    }

    const isFocused = useIsFocused();
    const cameraQueryResult = useQuery({
        queryKey: ['getCamerasMap', filters],
        queryFn: () => cameraService.getCamerasMap(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const { data } = cameraQueryResult;

    const poiQueryResult = useQuery({
        queryKey: ['getPointOfInterests'],
        queryFn: () => cameraService.getPointOfInterests(),
        enabled: !!authenticatedUser && isFocused,
    });

    const { data: poiData, refetch: poiRefetch } = poiQueryResult;

    function getMapLocation(event: LatLng) {
        if (createPointOfInterest) {
            setLatitude(String(event.lat));
            setLongitude(String(event.lng));
            setModalVisible(true);
        }
    }

    return (
        <Container>
            <View style={styles.content}>
                <View pointerEvents="box-none" style={styles.mapTopBar}>
                    {!windowInfo.isMobile && <MapSearchDropdown
                        filters={filters}
                        setFilters={setFilters}
                        setLocation={setLocation}
                    />}
                    <View style={{ maxWidth: 115 }}>
                        <MapFilter
                            setShowMapPoi={setShowMapPoi}
                            showMapPoi={showMapPoi}
                            filters={filters}
                            setFilters={setFilters}
                        />
                    </View>
                </View>
                {authenticatedUser?.isAdmin || authenticatedUser?.permissions.camera_management_point_of_interest ?
                    <View style={styles.newPointOfInterest}>
                        <TouchableOpacity onPress={() => {
                            setCreatePointOfInterest(true);
                            Toast.show({
                                type: 'sentinelxWarning',
                                text1: translate('clickOnMap'),
                            });
                        }}>
                            <Image source={require('../../../../assets/pin_point_of_interest.svg')} style={{ width: 22, height: 26 }} />
                        </TouchableOpacity>
                    </View>
                    : null}
                <View style={styles.mapBottomBar}>
                    <MapPolygonDropdown
                        dropDownDirection='TOP'
                        setPolygons={setPolygons}
                        zIndex={3}
                    />
                </View>
                <View style={{ flex: 1 }}>
                    <Map pinClick={pinClick}
                        dynamicPosition={location}
                        pinnedMarker={location}
                        cameraPositions={data}
                        pointsOfInterestPositions={poiData}
                        onClick={getMapLocation}
                        refreshPointsOfInterest={poiRefetch}
                        canManagePointsOfInterest={authenticatedUser?.isAdmin || authenticatedUser?.permissions.camera_management_point_of_interest}
                        polygons={polygons}
                        showMapPoi={showMapPoi}
                        isLoading={cameraQueryResult.isFetching}
                    />
                </View>
            </View>
            <PointOfInterestModal
                isModalVisible={isModalVisible}
                setModalVisible={setModalVisible}
                setCreatePointOfInterest={setCreatePointOfInterest}
                latitude={latitude}
                longitude={longitude}
                onClose={() => {
                    poiRefetch();
                }}
            />
        </Container >
    );
}

const styleSheet = createStyleSheet((theme) => ({
    content: {
        flex: 1,
        flexWrap: 'wrap'
    },
    mapTopBar: {
        position: 'absolute',
        flexDirection: 'row',
        zIndex: 1000,
        top: 10,
        paddingHorizontal: 10,
        flexWrap: 'wrap',
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-end',
        gap: 10
    },
    mapBottomBar: {
        position: 'absolute',
        zIndex: 1000,
        bottom: 10,
        paddingHorizontal: 10,
        flexWrap: 'wrap',
        display: 'flex',
        minWidth: 260
    },
    newPointOfInterest: {
        width: 40,
        height: 40,
        position: 'absolute',
        zIndex: 2,
        right: 10,
        bottom: 209,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.field.background,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.field.border,
    },
}));
